import { toNumber, toUtcDate } from '@rahtab/system/lib/tools/string';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { deserialize, serialize } from 'class-transformer';
import type { FactorInfo } from 'hooks/‌Bill/useInvoiceList';
import { Entity, SliceState, Statusable } from 'interfaces';
import moment from 'moment-jalaali';
import { RootState } from 'rahtab-redux';

const initialState: InvoicesSliceState = {
  loading: 'idle',
  hasNextPage: true,
  invoices: {},
};
export const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    invoicesLoading: (state, { payload }: PayloadAction<InvoicesSliceState['loading']>) => {
      state.loading = payload;
    },
    setHasNextPage: (state, { payload }: PayloadAction<InvoicesSliceState['hasNextPage']>) => {
      state.hasNextPage = payload;
    },
    addInvoice: (state, { payload }: PayloadAction<FactorInfo>) => {
      const items = payload.DeptList.map((item) => {
        const { Amount: quantity, MeasureName: unitText, Price: total, TemplateName: title } = item;
        const lineItem: InvoiceLineItem = {
          quantity: toNumber(quantity),
          unitText,
          title,
          total: toNumber(total),
        };
        return lineItem;
      });
      const invoice: Invoice = {
        id: payload.FactorCode,
        paidAt: <Date>(<unknown>serialize(toUtcDate(payload.PayDate, 'jYYYY/jMM/jDD'))),
        status: payload.PayFlag ? InvoiceStatus.paid : InvoiceStatus.unpaid,
        statusText: payload.PayTag,
        hasRequestedOfficialSaleBill: payload.HasOfficialFactorRequest,
        officialSaleBillUrl: `${payload.OfficialFactorId}`,
        from: <Date>(<unknown>serialize(toUtcDate(payload.FromDate, 'jYYYY/jMM/jDD'))),
        to: <Date>(<unknown>serialize(toUtcDate(payload.ToDate, 'jYYYY/jMM/jDD'))),
        total: toNumber(payload.TotalDept),
        items,
      };
      state.invoices[invoice.id] = invoice;
    },
  },
});

export const { invoicesLoading, addInvoice, setHasNextPage } = invoicesSlice.actions;

export default invoicesSlice.reducer;

export function invoiceSelector(ids: string): (state: RootState) => Invoice;
export function invoiceSelector(ids: string[]): (state: RootState) => Array<Invoice>;
export function invoiceSelector(ids: string | string[]) {
  return (state: RootState) => {
    const { invoices } = state;
    if (typeof ids === 'string') return getInvoice(ids);
    return ids.map((id) => getInvoice(id));
    function getInvoice(id: string): Invoice {
      return {
        ...invoices.invoices[id],
        paidAt: deserialize(Date, <string>(<unknown>invoices.invoices[id].paidAt)),
        from: deserialize(Date, <string>(<unknown>invoices.invoices[id].from)),
        to: deserialize(Date, <string>(<unknown>invoices.invoices[id].to)),
      };
    }
  };
}
type InvoicesSliceState = SliceState<'invoices', Invoice> & {
  hasNextPage: boolean;
};

export interface Invoice extends Entity, Statusable<typeof InvoiceStatus> {
  paidAt: Date;
  total: number;
  from: Date;
  to: Date;
  hasRequestedOfficialSaleBill: boolean;
  officialSaleBillUrl: string;
  items: InvoiceLineItem[];
}

export interface InvoiceLineItem {
  title: string;
  quantity: number;
  total: number;
  unitText: string;
}

const InvoiceStatus = {
  paid: 1,
  unpaid: 0,
} as const;
